import { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';

export interface UserProfile {
  id: string;
  email: string;
  first_name: string;
  last_name: string;
  role: string;
  department: string;
  start_date: string;
  holiday_allowance: number;
  permissions: Record<string, boolean>;
  last_login: string;
}

const DEFAULT_PERMISSIONS: Record<string, boolean> = {
  can_view_sales: false,
  can_view_spares: false,
  can_view_inventory: false,
  can_view_production: false,
  can_upload_payslips: false,
  can_approve_holidays: false,
  can_edit_sops: false,
  can_manage_staff: false,
};

export function useAuth() {
  const [user, setUser] = useState<any>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      if (session?.user) fetchProfile(session.user);
      else {
        setProfile(null);
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  async function fetchProfile(authUser: any) {
    try {
      const email = authUser.email?.toLowerCase() || '';
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('email', email)
        .single();

      if (error && error.code !== 'PGRST116') throw error;

      // If profile exists, use it. Otherwise create defaults.
      const isHardcodedAdmin = email === 'brad@vcsp.uk' || email === 'mac@vcsp.uk';

      if (data) {
        setProfile({
          ...data,
          permissions: data.permissions || (isHardcodedAdmin ? Object.fromEntries(Object.keys(DEFAULT_PERMISSIONS).map(k => [k, true])) : DEFAULT_PERMISSIONS),
          role: isHardcodedAdmin ? 'admin' : (data.role || 'staff'),
        });
      } else {
        setProfile({
          id: authUser.id,
          email,
          first_name: email.split('@')[0],
          last_name: '',
          role: isHardcodedAdmin ? 'admin' : 'staff',
          department: '',
          start_date: '',
          holiday_allowance: 25,
          permissions: isHardcodedAdmin ? Object.fromEntries(Object.keys(DEFAULT_PERMISSIONS).map(k => [k, true])) : DEFAULT_PERMISSIONS,
          last_login: new Date().toISOString(),
        });
      }

      // Update last_login silently
      try {
        await supabase.from('profiles').update({ last_login: new Date().toISOString() }).eq('email', email);
      } catch { /* silent */ }
    } catch (error) {
      console.error('Error fetching profile:', error);
    } finally {
      setLoading(false);
    }
  }

  const isAdmin = profile?.role === 'admin';
  const isManager = profile?.role === 'manager' || isAdmin;
  const hasPermission = (key: string) => isAdmin || (profile?.permissions?.[key] ?? false);

  return { user, profile, loading, isAdmin, isManager, hasPermission };
}